/**
 * Iter-157K — Unified mobile-viewport detector.
 *
 * Used by every PDF-rendering surface (DocumentViewerPage,
 * SchedulePreviewPage, ScriptPage etc.) to apply ONE consistent
 * rule:
 *
 *   Mobile → hand the PDF off to the device's native PDF viewer
 *            (new tab). Embedded iframes on a 390px viewport trap
 *            users with no pinch-to-zoom, no page nav, no rotation.
 *
 *   Desktop → keep the existing embedded viewer chrome — there's
 *            enough room for the embed to be readable.
 *
 * Breakpoint matches the Tailwind `md` threshold (768px) so the
 * client side handoff fires on phones / small portrait tablets and
 * the embed shows on landscape iPads / laptops / desktops.
 */
import { useEffect, useState } from "react";

const MOBILE_QUERY = "(max-width: 767px)";

function readMatch() {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
        return false;
    }
    return window.matchMedia(MOBILE_QUERY).matches;
}

export function useIsMobileViewport() {
    const [isMobile, setIsMobile] = useState(readMatch);
    useEffect(() => {
        if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
            return undefined;
        }
        const mq = window.matchMedia(MOBILE_QUERY);
        const handler = (e) => setIsMobile(e.matches);
        // Some Safari versions only support the legacy API.
        if (mq.addEventListener) {
            mq.addEventListener("change", handler);
            return () => mq.removeEventListener("change", handler);
        }
        mq.addListener(handler);
        return () => mq.removeListener(handler);
    }, []);
    return isMobile;
}
